import { Card, LoadingSpinner, Text } from "@clipboard-health/ui-components";
import { alpha, CardContent, LinearProgress, linearProgressClasses, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useGoToStripeExpressSignIn } from "@src/appV2/redesign/Accounts/PayoutSettings/hooks/useGoToStripeExpressSignIn";
import { Button } from "@src/appV2/redesign/components/Button";

function AboveThresholdTaxInformationCardContent(props: { earnedAmountInDollars: number }) {
  const { earnedAmountInDollars } = props;
  const { goToStripeExpressSignIn, isGoToStripeExpressSignInLoading } =
    useGoToStripeExpressSignIn();

  return (
    <CardContent sx={{ width: "100%" }}>
      <Stack direction="column" spacing={1} justifyContent="center" alignItems="center">
        <Text semibold variant="body2" color={(theme) => theme.palette.payment!.text}>
          You&apos;ve earned
        </Text>
        <Text variant="h3" color={(theme) => theme.palette.payment!.text}>
          {formatDollarsAsUsd(earnedAmountInDollars)}
        </Text>
      </Stack>

      <Stack direction="row" alignItems="center" marginTop={5}>
        <Text
          semibold
          variant="caption"
          textAlign="center"
          flex={1}
          sx={{
            color: (theme) => theme.palette.text.tertiary,
          }}
        >
          Your 1099-NEC form will be available by January 31.
        </Text>
      </Stack>

      <Stack direction="row" marginTop={3} justifyContent="center">
        <Button
          aria-label="Go to Stripe"
          size="small"
          variant="contained"
          disabled={isGoToStripeExpressSignInLoading}
          onClick={goToStripeExpressSignIn}
        >
          {isGoToStripeExpressSignInLoading ? <LoadingSpinner size="small" /> : "Go to Stripe"}
        </Button>
      </Stack>
    </CardContent>
  );
}

function BelowThresholdTaxInformationCardContent(props: {
  earnedAmountInDollars: number;
  formIssuanceThresholdInDollars: number;
}) {
  const { earnedAmountInDollars, formIssuanceThresholdInDollars } = props;

  const progress = Math.min((earnedAmountInDollars / formIssuanceThresholdInDollars) * 100, 100);

  return (
    <CardContent sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="column" spacing={1}>
          <Text semibold variant="body2" color={(theme) => theme.palette.payment!.text}>
            You&apos;ve earned
          </Text>
          <Text variant="h3" color={(theme) => theme.palette.payment!.text}>
            {formatDollarsAsUsd(earnedAmountInDollars)}
          </Text>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" marginTop={5}>
        <Stack direction="column" spacing={2} flex={1}>
          <LinearProgress
            variant="determinate"
            value={progress}
            color="success"
            sx={{
              height: 8,
              borderRadius: 8,
              backgroundColor: (theme) => alpha(theme.palette.intent?.success.text ?? "", 0.1),
              [`& .${linearProgressClasses.bar}`]: {
                borderRadius: 8,
                backgroundColor: (theme) => theme.palette.intent?.success.text,
              },
            }}
          />
          <Text
            semibold
            variant="caption"
            sx={{
              color: (theme) => theme.palette.text.tertiary,
            }}
          >
            You must earn{" "}
            {formatDollarsAsUsd(formIssuanceThresholdInDollars, { maximumFractionDigits: 0 })} in a
            year to receive a 1099-NEC form.
          </Text>
        </Stack>
      </Stack>
    </CardContent>
  );
}

export function TaxInformationCard(props: {
  isEligibleToPayTax: boolean;
  earnedAmountInDollars: number;
  formIssuanceThresholdInDollars: number;
}) {
  const { isEligibleToPayTax, earnedAmountInDollars, formIssuanceThresholdInDollars } = props;

  return (
    <Card
      sx={{
        borderRadius: (theme) => theme.borderRadius!.medium,
        display: "flex",
        alignItems: "center",
        background: (theme) => theme.palette.payment!.background,
        padding: (theme) => theme.spacing(3),
        flex: 1,
      }}
    >
      {isEligibleToPayTax ? (
        <AboveThresholdTaxInformationCardContent earnedAmountInDollars={earnedAmountInDollars} />
      ) : (
        <BelowThresholdTaxInformationCardContent
          earnedAmountInDollars={earnedAmountInDollars}
          formIssuanceThresholdInDollars={formIssuanceThresholdInDollars}
        />
      )}
    </Card>
  );
}
